/**
 * Fandetail (`/fans/show/:id`) — gemeten aan oud met scratchpad/subdump.py.
 *
 * Drie endpoints, geen polling:
 *   fans/{id}                     kern, engagement, chatter_snapshot, notes
 *   fans/{id}/conversations       de gesprekken van deze fan
 *   models/fans/{id}/earnings     omzet per type
 *
 * Blokken zoals oud: Fan Info · Engagement · Earnings Breakdown ·
 * Conversations (N) · Chatter Snapshot (bewerkbaar, met ↑/↓ en een eigen
 * veld) · Notes.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorNote, Loading, Panel, Plat, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { ModelNaam } from '../components/ModelNaam'
import { dt, eur, num } from '../lib/format'
import EigenVelden, { eigenVeldenUit } from '../components/EigenVelden'
import type { EigenVeld } from '../components/EigenVelden'

interface Fan {
  id: number
  fan_key: string
  fan_username: string | null
  fan_display_name: string | null
  fan_platform_id: string | null
  platform: string
  model_slug: string | null
  detected_language: string | null
  preferred_language: string | null
  engagement_level: string | null
  total_spend: number
  is_vip: boolean
  is_excluded_from_ai: boolean
  first_seen_at: string | null
  last_active_at: string | null
  custom_notes: string | null
  chatter_snapshot: Record<string, unknown> | null
  fan_info: Record<string, unknown> | null
}

interface Convo {
  id: number
  platform: string
  model_slug: string
  model_name: string | null
  status: string
  current_tag: string | null
  total_messages: number
  fan_messages: number
  ai_messages: number
  last_message: string | null
  last_message_at: string | null
}

interface Earn {
  total_revenue: number
  net_revenue: number
  total_transactions: number
  by_type: Record<string, { total: number; net: number; count: number }>
}

/** Profielvelden, in de volgorde van het oude scherm. */
const INFO_FIELDS: [string, string][] = [
  ['name', 'Name / Username (real)'],
  ['age', 'Age'],
  ['location', 'Location / Country'],
  ['timezone', 'Timezone'],
  ['work', 'Work / Studies'],
  ['relationship', 'Relationship status'],
  ['hobbies', 'Hobbies / Lifestyle'],
  ['preferences', 'Preferences (general)'],
  ['interests', 'Personal interests'],
  ['enjoys_in_chats', 'What he enjoys in chats'],
  ['important_events', 'Important events'],
  ['memory_points', 'Memory points'],
]

/** De Chatter Snapshot-velden, met de hints die oud toont. */
const SNAP_FIELDS: [string, string][] = [
  ['summary', 'Quick summary'],
  ['recent_topics', 'Recently talked about'],
  ['intent', 'Intent (e.g. chatting + slowly build sexual tension)'],
  ['spender_level', 'Spender level (low / medium / high)'],
  ['do', 'DO'],
  ['dont', "DON'T"],
  ['hook', 'Hook'],
  ['next_move', 'Next move'],
]

export default function FanShow() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { run, busy } = useActions()
  const [draft, setDraft] = useState<Record<string, string> | null>(null)
  const [order, setOrder] = useState<string[] | null>(null)
  const [notes, setNotes] = useState<string | null>(null)
  /** 07-10-2026 (punt 5): eigen velden, dezelfde als in de chat. */
  const [eigen, setEigen] = useState<EigenVeld[] | null>(null)

  const f = useApi<Fan>(`/api/admin/fans/${id}`, [id])
  const convos = useApi<Convo[]>(`/api/admin/fans/${id}/conversations`, [id])
  const earn = useApi<Earn>(`/api/admin/models/fans/${id}/earnings`, [id])

  const fan = f.data
  const fields = useMemo(() => {
    if (draft) return draft
    const src = { ...(fan?.fan_info ?? {}), ...(fan?.chatter_snapshot ?? {}) } as Record<string, unknown>
    const out: Record<string, string> = {}
    for (const [k] of [...INFO_FIELDS, ...SNAP_FIELDS]) out[k] = String(src[k] ?? '')
    for (const [k, v] of Object.entries(src)) {
      if (!k.startsWith('_') && !(k in out)) out[k] = String(v ?? '')
    }
    return out
  }, [draft, fan])

  const keys = useMemo(() => {
    if (order) return order
    const base = INFO_FIELDS.map(([k]) => k)
    const extra = Object.keys(fields).filter(
      (k) => !base.includes(k) && !SNAP_FIELDS.some(([s]) => s === k))
    return [...base, ...extra]
  }, [order, fields])

  if (f.loading && !f.data) return <Loading />
  if (f.error) return <ErrorNote error={f.error} />
  if (!fan) return <ErrorNote error="Fan not found." />

  const label = (k: string) =>
    INFO_FIELDS.find(([x]) => x === k)?.[1] ?? k
  const move = (i: number, d: -1 | 1) => {
    const next = [...keys]
    const j = i + d
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j], next[i]]
    setOrder(next)
  }

  const snapshotBody = () => {
    const info: Record<string, string> = {}
    const snap: Record<string, string> = {}
    for (const k of keys) info[k] = fields[k] ?? ''
    for (const [k] of SNAP_FIELDS) snap[k] = fields[k] ?? ''
    const lijst = eigen ?? eigenVeldenUit(fan?.fan_info as Record<string, unknown> | null)
    return {
      fan_info: { ...info, _custom_fields: lijst.filter((v) => v.label.trim() || v.value.trim()) },
      chatter_snapshot: snap,
    }
  }

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button" onClick={() => navigate('/fans')}>
          Fans
        </button>
        <button className="btn ghost sm" type="button"
                onClick={() => { f.refetch(); convos.refetch(); earn.refetch() }}>
          Refresh
        </button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Panel
          title="Fan Info"
          right={
            <button className="btn ghost sm" type="button"
                    onClick={() => navigate(`/conversations?fan=${encodeURIComponent(
                      fan.fan_platform_id || fan.fan_username || '')}`)}>
              View Conversations
            </button>
          }
        >
          <div className="kv">
            <span>Username</span><span>{fan.fan_username ?? 'N/A'}</span>
            <span>Display Name</span><span>{fan.fan_display_name ?? 'N/A'}</span>
            <span>Platform</span><span>{fan.platform}</span>
            <span>Model</span><span>{fan.model_slug ?? '—'}</span>
            <span>Platform ID</span><span>{fan.fan_platform_id ?? '—'}</span>
            <span>Language</span>
            <span>{fan.detected_language || fan.preferred_language || 'Unknown'}</span>
          </div>
        </Panel>

        <Panel title="Engagement">
          <div className="kv">
            <span>Level</span><span>{fan.engagement_level ?? '—'}</span>
            <span>Total Spend</span><span className="mono">{eur(fan.total_spend)}</span>
            <span>VIP</span><span>{fan.is_vip ? 'Yes' : 'No'}</span>
            <span>AI Enabled</span>
            <span>
              {fan.is_excluded_from_ai
                ? <Tag kind="mut">Off</Tag>
                : <Tag kind="ok">On</Tag>}
            </span>
            <span>First Seen</span><span>{dt(fan.first_seen_at)}</span>
            <span>Last Active</span><span>{dt(fan.last_active_at)}</span>
          </div>
        </Panel>
      </div>

      <Panel title="Earnings Breakdown">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
          <div className="card kpi">
            <div className="lbl">Total Revenue</div>
            <div className="val">{eur(earn.data?.total_revenue)}</div>
          </div>
          <div className="card kpi">
            <div className="lbl">Net</div>
            <div className="val">{eur(earn.data?.net_revenue)}</div>
          </div>
          <div className="card kpi">
            <div className="lbl">Transactions</div>
            <div className="val">{num(earn.data?.total_transactions)}</div>
          </div>
        </div>
        {Object.keys(earn.data?.by_type ?? {}).length ? (
          <Table
            cols={[
              { key: 't', label: 'Type' }, { key: 'g', label: 'Gross', num: true },
              { key: 'n', label: 'Net', num: true }, { key: 'c', label: 'Count', num: true },
            ]}
          >
            {Object.entries(earn.data?.by_type ?? {}).map(([k, v]) => (
              <tr key={k}>
                <td className="mut">{k}</td>
                <td className="num mono">{eur(v.total)}</td>
                <td className="num mono">{eur(v.net)}</td>
                <td className="num mono">{num(v.count)}</td>
              </tr>
            ))}
          </Table>
        ) : <div className="empty" style={{ marginTop: 12 }}>No transactions for this fan.</div>}
      </Panel>

      <Panel title={`Conversations (${convos.data?.length ?? 0})`} flush>
        <Table
          cols={[
            { key: 'm', label: 'Model' }, { key: 'p', label: 'Platform' },
            { key: 't', label: 'Tag' }, { key: 'n', label: 'Msgs', num: true },
            { key: 'l', label: 'Last message' }, { key: 'a', label: '' },
          ]}
          emptyText="This fan has no conversations."
        >
          {(convos.data ?? []).map((c) => (
            <tr key={c.id}>
              <td><ModelNaam slug={c.model_slug} naam={c.model_name} vet /></td>
              <td><Plat value={c.platform} /></td>
              <td>{c.current_tag ? <Tag kind="warn">{c.current_tag}</Tag> : <span className="dim">—</span>}</td>
              <td className="num mono">{num(c.total_messages)}</td>
              <td className="mut" style={{ maxWidth: 320 }}>{c.last_message}</td>
              <td>
                <button className="btn ghost sm" type="button"
                        onClick={() => navigate(`/conversations/show/${c.id}`)}>
                  Open
                </button>
              </td>
            </tr>
          ))}
        </Table>
      </Panel>

      {/* ---- profiel + snapshot, bewerkbaar ---- */}
      <Panel
        title="Chatter Snapshot"
        desc={draft || order ? 'Unsaved changes' : undefined}
        right={
          <>
            <button
              className="btn primary sm"
              type="button"
              disabled={!(draft || order || eigen) || busy === 'Fan profile'}
              onClick={() => run({
                label: 'Fan profile',
                request: mut.patch(`/api/admin/fans/${id}`, snapshotBody()),
                onDone: () => { setDraft(null); setOrder(null); setEigen(null); f.refetch() },
                successText: 'Saved.',
              })}
            >
              Save
            </button>
          </>
        }
      >
        {keys.map((k, i) => (
          <div className="field" key={k}>
            <label>
              {label(k)}
              <span className="row" style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
                <button className="btn ghost sm" type="button"
                        onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                <button className="btn ghost sm" type="button"
                        onClick={() => move(i, 1)} disabled={i === keys.length - 1}>↓</button>
              </span>
            </label>
            <input className="inp" value={fields[k] ?? ''}
                   onChange={(e) => setDraft({ ...fields, [k]: e.target.value })} />
          </div>
        ))}

        <div className="field">
          <label>Custom fields</label>
          <EigenVelden velden={eigen ?? eigenVeldenUit(fan.fan_info as Record<string, unknown> | null)}
                       onChange={setEigen} />
        </div>

        <div className="divh" />
        {SNAP_FIELDS.map(([k, lab]) => (
          <div className="field" key={k}>
            <label>{lab}</label>
            <textarea className="inp" rows={k === 'summary' || k === 'recent_topics' ? 2 : 1}
                      value={fields[k] ?? ''}
                      onChange={(e) => setDraft({ ...fields, [k]: e.target.value })} />
          </div>
        ))}
      </Panel>

      <Panel
        title="Notes"
        right={
          <button
            className="btn primary sm"
            type="button"
            disabled={notes === null || busy === 'Notes'}
            onClick={() => run({
              label: 'Notes',
              request: mut.patch(`/api/admin/fans/${id}`, { custom_notes: notes }),
              onDone: () => { setNotes(null); f.refetch() },
              successText: 'Notes saved.',
            })}
          >
            Save Notes
          </button>
        }
      >
        <textarea className="inp" rows={4} value={notes ?? fan.custom_notes ?? ''}
                  onChange={(e) => setNotes(e.target.value)} />
      </Panel>
    </>
  )
}
